/**
 * Steering from the keyboard (A/D, arrows: full left/right) or by dragging a
 * finger anywhere on the screen: slide left or right of where you touched,
 * further = harder, for smooth analog control. A quick tap paddles. Paddle is
 * also Space/↑/W or the on-screen button. `confirm` (Enter/tap) starts and
 * restarts.
 */
export type InputAction = 'confirm' | 'restart' | 'paddle' | 'pause' | 'item';

/** Finger travel (px) for a full-strength nudge; scales down on small screens. */
const DRAG_FULL = 64;
/** A touch shorter and stiller than this is a tap (paddle), not a steer. */
const TAP_MS = 220;
const TAP_SLOP = 12;

type Drag = { anchorX: number; startX: number; startY: number; x: number; t0: number; moved: boolean };

export class SteerInput {
  private readonly keys = new Set<string>();
  private readonly drags = new Map<number, Drag>();
  /** Where the active drag is, for the on-screen thumb ring. */
  onDrag: ((drag: { anchorX: number; y: number; steer: number } | null) => void) | null = null;

  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.repeat) return;
    this.keys.add(event.code);
    if (event.code === 'Enter') this.onAction('confirm');
    if (event.code === 'Space' || event.code === 'ArrowUp' || event.code === 'KeyW') {
      event.preventDefault();
      this.onAction('paddle');
    }
    if (event.code === 'KeyR') this.onAction('restart');
    if (event.code === 'Escape' || event.code === 'KeyP') this.onAction('pause');
    if (event.code === 'KeyE' || event.code === 'ShiftLeft' || event.code === 'ShiftRight') this.onAction('item');
  };

  private readonly onKeyUp = (event: KeyboardEvent) => {
    this.keys.delete(event.code);
  };

  private readonly onPointerDown = (event: PointerEvent) => {
    event.preventDefault();
    const x = event.clientX;
    this.drags.set(event.pointerId, { anchorX: x, startX: x, startY: event.clientY, x, t0: performance.now(), moved: false });
    this.publishDrag(event.pointerId, event.clientY);
    this.onAction('confirm');
  };

  private readonly onPointerMove = (event: PointerEvent) => {
    const drag = this.drags.get(event.pointerId);
    if (!drag) return;
    drag.x = event.clientX;
    if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) > TAP_SLOP) drag.moved = true;
    // A floating anchor: dragging past full strength drags the anchor along,
    // so reversing direction responds at once instead of unwinding first.
    const full = this.dragFull();
    if (drag.x - drag.anchorX > full) drag.anchorX = drag.x - full;
    if (drag.anchorX - drag.x > full) drag.anchorX = drag.x + full;
    this.publishDrag(event.pointerId, event.clientY);
  };

  private readonly onPointerUp = (event: PointerEvent) => {
    const drag = this.drags.get(event.pointerId);
    this.drags.delete(event.pointerId);
    if (this.drags.size === 0) this.onDrag?.(null);
    if (drag && event.type === 'pointerup' && !drag.moved && performance.now() - drag.t0 < TAP_MS) this.onAction('paddle');
  };

  private readonly onPaddleDown = (event: PointerEvent) => {
    event.preventDefault();
    this.onAction('paddle');
  };

  private readonly onBlur = () => {
    this.keys.clear();
    this.drags.clear();
    this.onDrag?.(null);
  };

  constructor(
    private readonly surface: HTMLElement,
    private readonly paddleButton: HTMLElement,
    private readonly onAction: (kind: InputAction) => void,
  ) {
    paddleButton.addEventListener('pointerdown', this.onPaddleDown);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    surface.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  /** -1 = full nudge left, 1 = full nudge right; drags give anything between. */
  readSteer(): number {
    let steer = 0;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) steer -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) steer += 1;
    for (const drag of this.drags.values()) steer += this.dragSteer(drag);
    return Math.max(-1, Math.min(1, steer));
  }

  private dragFull(): number {
    return Math.min(DRAG_FULL, window.innerWidth * 0.16);
  }

  private dragSteer(drag: Drag): number {
    const t = Math.max(-1, Math.min(1, (drag.x - drag.anchorX) / this.dragFull()));
    // A small dead zone so resting a thumb doesn't wobble the duck.
    return Math.abs(t) < 0.08 ? 0 : t;
  }

  private publishDrag(id: number, y: number): void {
    const drag = this.drags.get(id);
    if (drag && this.onDrag) this.onDrag({ anchorX: drag.anchorX, y, steer: this.dragSteer(drag) });
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.surface.removeEventListener('pointerdown', this.onPointerDown);
    this.paddleButton.removeEventListener('pointerdown', this.onPaddleDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  }
}
